import { useCallback, useEffect, useMemo, useState } from "react";
import { computeAll } from "../lib/calculations";
import { buildInsights, buildOptimizations } from "../lib/diagnostics";
import { DEFAULT_VACANCE, type SimulationInput } from "../lib/types";

const STORAGE_KEY = "rentalmnp:simulation:v1";

export const DEFAULT_INPUT: SimulationInput = {
  prixAcquisition: 250000,
  travaux: 0,
  mobilier: 12500, // 5% par défaut
  region: "Île-de-France",
  loyerMensuel: 1200,
  typeLocation: "longue",
  tauxVacance: DEFAULT_VACANCE.longue,
  chargesMode: "simple",
  chargesPct: 30,
  chargesDetail: {
    taxeFonciere: 1200,
    assurancePNO: 180,
    chargesCopro: 600,
    entretien: 400,
    cfe: 350,
    fraisGestionPct: 0,
    comptabilite: 0,
    autres: 0,
  },
  apport: 50000,
  hasCredit: false,
  montantEmprunte: 200000,
  tauxAnnuel: 3.8,
  dureeAnnees: 20,
  assuranceEmprunteurPct: 0.3,
  tmi: 0.30,
  inflationLoyers: 1.5,
  inflationCharges: 2,
  inflationBien: 2,
};

function loadStored(): SimulationInput | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SimulationInput>;
    return { ...DEFAULT_INPUT, ...parsed, chargesDetail: { ...DEFAULT_INPUT.chargesDetail, ...(parsed.chargesDetail ?? {}) } };
  } catch {
    return null;
  }
}

export function useSimulation() {
  const [input, setInput] = useState<SimulationInput>(DEFAULT_INPUT);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate depuis localStorage côté client
  useEffect(() => {
    const stored = loadStored();
    if (stored) setInput(stored);
    setHydrated(true);
  }, []);

  // Persist
  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(input));
    } catch {
      // ignore quota errors
    }
  }, [input, hydrated]);

  const update = useCallback(<K extends keyof SimulationInput>(key: K, value: SimulationInput[K]) => {
    setInput((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateDetail = useCallback(
    <K extends keyof SimulationInput["chargesDetail"]>(key: K, value: number) => {
      setInput((prev) => ({
        ...prev,
        chargesDetail: { ...prev.chargesDetail, [key]: value },
      }));
    },
    []
  );

  const reset = useCallback(() => {
    setInput(DEFAULT_INPUT);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* noop */
      }
    }
  }, []);

  const results = useMemo(() => computeAll(input), [input]);
  const insights = useMemo(() => buildInsights(input, results), [input, results]);
  const optimizations = useMemo(
    () => buildOptimizations(input, results),
    [input, results]
  );

  return { input, setInput, update, updateDetail, reset, results, insights, optimizations };
}
